import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Wallet, ChevronDown, ChevronUp, ArrowRightLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import BlueCoinsCard from '../BlueCoinsCard';

const YELLOW_COIN_IMG = 'https://customer-assets.emergentagent.com/job_8f198eda-3cd9-45f6-a741-5b80db2d25c7/artifacts/uhy6yxmh_ChatGPT%20Image%2015%20de%20fev.%20de%202026%2C%2013_18_27.png';

/**
 * Aba Carteira do afiliado — saldo, resgate (modal) e extrato dentro do card.
 */
// Celular em pt-BR digita "1.000" para mil — e o campo numérico lia 1. Aqui só os dígitos valem.
const soDigitos = (texto) => {
  const d = String(texto ?? '').normalize('NFKC').replace(/\D/g, '');
  return d ? parseInt(d, 10) : NaN;
};
const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

const WalletTab = ({
  wallet,
  walletTx = [],
  walletRedeemAmount,
  setWalletRedeemAmount,
  onRedeem,
  redeeming,
  onExchange,
  exchanging,
  blueRefresh = 0,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [exchangeConfirmOpen, setExchangeConfirmOpen] = useState(false);
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [showTx, setShowTx] = useState(false);
  const [txFilter, setTxFilter] = useState('all'); // all | credit | redemption | exchange

  if (!wallet) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Wallet className="w-12 h-12 mx-auto mb-2 text-gray-400" />
        <p>Carregando carteira...</p>
      </div>
    );
  }

  const parsedAmount = soDigitos(walletRedeemAmount);
  const hasDailyLimit = (wallet.max_daily_redemption_coins || 0) > 0;
  const remainingToday = hasDailyLimit ? (wallet.remaining_today ?? wallet.max_daily_redemption_coins) : null;
  const dailyLimitReached = hasDailyLimit && remainingToday <= 0;
  const isValidAmount =
    !!parsedAmount &&
    parsedAmount >= wallet.min_redemption_coins &&
    parsedAmount <= wallet.wallet_balance &&
    (!hasDailyLimit || parsedAmount <= remainingToday);
  // por que o botão está desabilitado — a pessoa precisa VER o motivo
  const motivoResgate = !walletRedeemAmount ? ''
    : !parsedAmount ? 'Digite só números, sem ponto ou vírgula (ex.: 1000).'
      : parsedAmount < wallet.min_redemption_coins ? `O resgate mínimo é ${fmt(wallet.min_redemption_coins)} moedas.`
        : parsedAmount > wallet.wallet_balance ? `Você tem ${fmt(wallet.wallet_balance)} moedas — não dá para resgatar mais do que isso.`
          : (hasDailyLimit && parsedAmount > remainingToday) ? `Hoje ainda dá para resgatar ${fmt(remainingToday)} moedas.`
            : '';

  const handleClickRedeem = () => {
    if (!isValidAmount) return; // toast é emitido pelo pai em onRedeem quando inválido
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setRedeemOpen(false);
    await onRedeem(parsedAmount);
  };

  // Intercâmbio amarelas → Azulzinhas (dedução de 10%)
  const parsedExchange = soDigitos(exchangeAmount);
  const minExchange = wallet.min_exchange_coins || 100;
  const exchangeAvailable = wallet.exchange_enabled && wallet.exchange_event_active;
  const bluePreview = parsedExchange > 0 ? Math.floor((parsedExchange * 9) / 10) : 0;
  const isValidExchange =
    !!parsedExchange &&
    parsedExchange >= minExchange &&
    parsedExchange % 10 === 0 &&
    parsedExchange <= wallet.wallet_balance;

  const handleExchangeConfirm = async () => {
    setExchangeConfirmOpen(false);
    setExchangeOpen(false);
    await onExchange(parsedExchange);
    setExchangeAmount('');
  };

  const filteredTx = txFilter === 'all' ? walletTx : walletTx.filter((tx) => tx.type === txFilter);
  const filterPill = (key, label, cls) => (
    <button key={key} onClick={() => setTxFilter(key)}
      data-testid={`wallet-tx-filter-${key}`}
      className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${txFilter === key ? cls : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Azulzinhas 💙 */}
      <BlueCoinsCard meUrl="/api/blue-coins/reseller/me" ledgerUrl="/api/blue-coins/reseller/me/ledger" tokenKey="reseller_token" refreshKey={blueRefresh} />

      {/* Card do saldo (mesmo padrão do card de Azulzinhas) */}
      <Card
        data-testid="wallet-balance-card"
        className="rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 via-white to-yellow-50 shadow-[0_8px_32px_rgba(217,119,6,0.10)]"
      >
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src={YELLOW_COIN_IMG} alt="Moedas"
                className="h-12 w-12 flex-shrink-0 object-contain drop-shadow-md" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600">Saldo da Carteira</p>
                <p className="font-display text-2xl font-black text-amber-700" data-testid="wallet-balance-value">
                  {wallet.wallet_balance.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowTx(!showTx)}
                data-testid="wallet-toggle-tx"
                className="rounded-full border-amber-200 bg-white/80 text-xs text-amber-700 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-amber-50">
                Extrato {showTx ? <ChevronUp className="ml-1 h-3.5 w-3.5" /> : <ChevronDown className="ml-1 h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" onClick={() => setExchangeOpen(true)}
                data-testid="wallet-open-exchange"
                className="rounded-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white text-xs shadow-md shadow-blue-300/50 transition-all duration-200 ease-out hover:-translate-y-0.5">
                <ArrowRightLeft className="mr-1 h-3.5 w-3.5" /> Intercâmbio
              </Button>
              <Button size="sm" onClick={() => setRedeemOpen(true)}
                data-testid="wallet-open-redeem"
                className="rounded-full bg-gradient-to-r from-amber-400 to-yellow-600 hover:from-amber-500 hover:to-yellow-700 text-white text-xs shadow-md shadow-amber-300/50 transition-all duration-200 ease-out hover:-translate-y-0.5">
                Resgatar
              </Button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Comissões das suas vendas — resgate direto para a sua conta KAKO.
            {!wallet.commissions_enabled && (
              <span className="ml-2 inline-block bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded font-semibold">
                Comissões pausadas
              </span>
            )}
          </p>

          {/* Extrato dentro do próprio card */}
          {showTx && (
            <div className="custom-scrollbar mt-3 rounded-xl border border-amber-100 bg-white/70" data-testid="wallet-tx-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap border-b border-amber-50 px-3 py-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {filterPill('all', 'Todas', 'bg-amber-100 text-amber-700')}
                  {filterPill('credit', '↑ Entradas', 'bg-emerald-100 text-emerald-700')}
                  {filterPill('redemption', '↓ Saídas', 'bg-orange-100 text-orange-700')}
                  {filterPill('exchange', '🔄 Intercâmbio', 'bg-sky-100 text-sky-700')}
                </div>
                <span className="text-[11px] text-gray-500" data-testid="wallet-tx-count">
                  {filteredTx.length} registro(s)
                </span>
              </div>
              {filteredTx.length === 0 ? (
                <p className="text-center text-gray-500 text-xs py-6" data-testid="wallet-tx-empty">
                  {walletTx.length === 0 ? 'Sem movimentações ainda.'
                    : txFilter === 'credit' ? 'Nenhuma entrada ainda.'
                    : txFilter === 'exchange' ? 'Nenhum intercâmbio ainda.' : 'Nenhuma saída ainda.'}
                </p>
              ) : (
                <div className="custom-scrollbar max-h-64 overflow-y-auto pr-1" data-testid="wallet-tx-scroll">
                  {filteredTx.map((tx) => {
                    const dt = new Date(tx.created_at);
                    const dateStr = dt.toLocaleDateString('pt-BR');
                    const timeStr = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div
                        key={tx.id}
                        className="px-3 py-2.5 flex items-center justify-between gap-3 border-b border-amber-50 last:border-0"
                        data-testid={`wallet-tx-${tx.type}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {tx.type === 'credit' ? (
                              <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                Crédito
                              </span>
                            ) : tx.type === 'exchange' ? (
                              <span className="text-[11px] font-semibold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">
                                Intercâmbio
                              </span>
                            ) : (
                              <span className="text-[11px] font-semibold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                Resgate
                              </span>
                            )}
                            <span className="text-[11px] text-gray-500 whitespace-nowrap">
                              {dateStr} · {timeStr}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5 truncate">
                            {tx.type === 'credit' ? (
                              <>
                                Venda de {(tx.sale_coins || 0).toLocaleString()} moedas
                                {tx.order_id && (
                                  <span className="ml-1 font-mono text-gray-400">#{tx.order_id.slice(0, 8)}</span>
                                )}
                              </>
                            ) : tx.type === 'exchange' ? (
                              <>Trocou por {(tx.blue_coins || 0).toLocaleString()} Azulzinhas 💙</>
                            ) : (
                              <>Enviado para KAKO {tx.kako_target_id || wallet.kako_id}</>
                            )}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`font-bold text-sm whitespace-nowrap flex items-center justify-end gap-1 ${tx.type === 'credit' ? 'text-emerald-600' : tx.type === 'exchange' ? 'text-sky-600' : 'text-orange-600'}`}>
                            {tx.type === 'credit' ? '+' : '−'}{tx.coins.toLocaleString()}
                            <img src={YELLOW_COIN_IMG} alt="" className="h-4 w-4 object-contain inline-block" />
                          </p>
                          <p className="text-[10px] text-gray-400 whitespace-nowrap">Saldo: {tx.balance_after.toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de resgate */}
      <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
        <DialogContent data-testid="wallet-redeem-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={YELLOW_COIN_IMG} alt="" className="h-7 w-7 object-contain" />
              Resgatar moedas
            </DialogTitle>
            <DialogDescription>
              As moedas vão direto para a sua conta KAKO <b>{wallet.kako_id}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2">
              <span className="text-xs text-amber-700 font-semibold">Saldo disponível</span>
              <span className="font-black text-amber-700" data-testid="wallet-redeem-balance">
                {fmt(wallet.wallet_balance)}
              </span>
            </div>
            <Input
              data-testid="wallet-redeem-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={`Mín. ${fmt(wallet.min_redemption_coins)}`}
              value={walletRedeemAmount}
              onChange={(e) => setWalletRedeemAmount(e.target.value.normalize('NFKC').replace(/\D/g, '').slice(0, 9))}
              disabled={!wallet.redemptions_enabled || wallet.wallet_balance < wallet.min_redemption_coins || dailyLimitReached}
              className="h-10 text-sm rounded-xl border-amber-200 focus-visible:ring-amber-400"
            />
            {isValidAmount && (
              <div className="flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2" data-testid="wallet-redeem-preview">
                <span className="text-xs text-emerald-700 font-semibold">Você vai receber</span>
                <span className="font-black text-emerald-700">{fmt(parsedAmount)} moedas</span>
              </div>
            )}
            {wallet.redemptions_enabled && !dailyLimitReached && wallet.wallet_balance >= wallet.min_redemption_coins && motivoResgate && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-redeem-reason">
                {motivoResgate}
              </p>
            )}
            {!wallet.redemptions_enabled && (
              <p className="text-[11px] text-red-600 font-medium">
                ⚠️ Resgates temporariamente desabilitados pelo administrador
              </p>
            )}
            {wallet.redemptions_enabled && wallet.wallet_balance < wallet.min_redemption_coins && (
              <p className="text-[11px] text-gray-500">
                Você precisa de pelo menos {wallet.min_redemption_coins.toLocaleString()} moedas para resgatar.
              </p>
            )}
            {wallet.redemptions_enabled && hasDailyLimit && dailyLimitReached && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-daily-limit-reached">
                ⚠️ Limite diário de {wallet.max_daily_redemption_coins.toLocaleString()} moedas atingido. Volte amanhã!
              </p>
            )}
            {wallet.redemptions_enabled && hasDailyLimit && !dailyLimitReached && (
              <p className="text-[11px] text-gray-500" data-testid="wallet-daily-limit-info">
                Limite diário: {wallet.max_daily_redemption_coins.toLocaleString()} moedas ·
                Ainda pode resgatar hoje: <b className="text-amber-700">{remainingToday.toLocaleString()}</b>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              data-testid="wallet-redeem-btn"
              onClick={handleClickRedeem}
              disabled={
                redeeming ||
                !wallet.redemptions_enabled ||
                wallet.wallet_balance < wallet.min_redemption_coins ||
                dailyLimitReached ||
                !isValidAmount
              }
              className="w-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-600 hover:from-amber-500 hover:to-yellow-700 text-white shadow-md shadow-amber-300/50"
            >
              {redeeming ? '...' : 'Resgatar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de intercâmbio */}
      <Dialog open={exchangeOpen} onOpenChange={setExchangeOpen}>
        <DialogContent data-testid="wallet-exchange-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-sky-600" />
              Intercâmbio 💙
            </DialogTitle>
            <DialogDescription>
              Troque moedas amarelas por <b>Azulzinhas</b> com dedução de 10%.
              Ex: 100 amarelas → 90 Azulzinhas. A troca não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2">
              <span className="text-xs text-amber-700 font-semibold">Saldo disponível</span>
              <span className="font-black text-amber-700" data-testid="wallet-exchange-balance">
                {fmt(wallet.wallet_balance)}
              </span>
            </div>
            <Input
              data-testid="wallet-exchange-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={`Mín. ${fmt(minExchange)} (múltiplos de 10)`}
              value={exchangeAmount}
              onChange={(e) => setExchangeAmount(e.target.value.normalize('NFKC').replace(/\D/g, '').slice(0, 9))}
              disabled={!exchangeAvailable || wallet.wallet_balance < minExchange}
              className="h-10 text-sm rounded-xl border-sky-200 focus-visible:ring-sky-400"
            />
            {parsedExchange > 0 && (
              <div className="flex items-center justify-between rounded-xl border border-sky-100 bg-sky-50/70 px-3 py-2" data-testid="wallet-exchange-preview">
                <span className="text-xs text-sky-700 font-semibold">Você recebe</span>
                <span className="font-black text-sky-700">{bluePreview.toLocaleString()} 💙</span>
              </div>
            )}
            {!wallet.exchange_enabled && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-exchange-disabled">
                ⚠️ Intercâmbio temporariamente desabilitado pelo administrador
              </p>
            )}
            {wallet.exchange_enabled && !wallet.exchange_event_active && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-exchange-no-event">
                ⚠️ Intercâmbio disponível apenas com o evento da Roleta ativo
              </p>
            )}
            {exchangeAvailable && exchangeAmount && !parsedExchange && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-exchange-digits-error">
                Digite só números, sem ponto ou vírgula (ex.: 1000).
              </p>
            )}
            {exchangeAvailable && parsedExchange > wallet.wallet_balance && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-exchange-over-error">
                Você tem {fmt(wallet.wallet_balance)} moedas — não dá para trocar mais do que isso.
              </p>
            )}
            {exchangeAvailable && parsedExchange > 0 && parsedExchange < minExchange && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-exchange-min-error">
                O mínimo para trocar é {fmt(minExchange)} moedas.
              </p>
            )}
            {exchangeAvailable && parsedExchange > 0 && parsedExchange % 10 !== 0 && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wallet-exchange-multiple-error">
                O valor deve ser múltiplo de 10 (ex: 100, 250, 1000).
              </p>
            )}
            {exchangeAvailable && wallet.wallet_balance < minExchange && (
              <p className="text-[11px] text-gray-500">
                Você precisa de pelo menos {minExchange.toLocaleString()} moedas para fazer intercâmbio.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              data-testid="wallet-exchange-btn"
              onClick={() => isValidExchange && setExchangeConfirmOpen(true)}
              disabled={exchanging || !exchangeAvailable || !isValidExchange}
              className="w-full rounded-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-md shadow-blue-300/50"
            >
              {exchanging ? '...' : 'Trocar por Azulzinhas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de intercâmbio */}
      <AlertDialog open={exchangeConfirmOpen} onOpenChange={setExchangeConfirmOpen}>
        <AlertDialogContent data-testid="wallet-exchange-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar intercâmbio?</AlertDialogTitle>
            <AlertDialogDescription>
              Você vai trocar <b>{(parsedExchange || 0).toLocaleString()}</b> moedas amarelas por
              <b> {bluePreview.toLocaleString()} Azulzinhas 💙</b> (dedução de 10%).
              Essa troca é imediata e não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wallet-exchange-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="wallet-exchange-confirm-btn"
              onClick={handleExchangeConfirm}
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
            >
              Confirmar intercâmbio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmação de resgate */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="wallet-redeem-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar resgate?</AlertDialogTitle>
            <AlertDialogDescription>
              Vamos enviar <b>{(parsedAmount || 0).toLocaleString()}</b> moedas para a sua conta KAKO
              <b> {wallet.kako_id}</b>. Essa ação é imediata e não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wallet-redeem-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="wallet-redeem-confirm-btn"
              onClick={handleConfirm}
              className="bg-gradient-to-r from-amber-400 to-yellow-600 hover:from-amber-500 hover:to-yellow-700"
            >
              Confirmar resgate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WalletTab;
